import React, { useState, useCallback, useRef, useContext } from 'react';
import classnames from 'classnames';
import { MobileContext } from '../../Context';
import { useScrollAware } from '../../utils/hooks/useScrollAware';
import { useTimedValue } from '../../utils/hooks/useTimedValue';
import ArrowDown from '../../Icons/ArrowDown';
import { IconSize } from '../../Icons/IconWrap';
import { scrollIntoViewInViewportCenter } from '../../utils/scrollIntoViewWithHeader';
import PlainButtonOrLink from '../../Buttons/PlainButtonOrLink';
import s from './ScrollDownButton.scss';
/** A component with two main elements:
 *  - A button (meant to be position: fixed in the viewport)
 *  - A target that will be scrolled to when the button is clicked
 *  Place this component in the tree where you want the container to scroll to
 *  when the button is clicked.
 */
export var ScrollDownButton = function (_a) {
    var className = _a.className, onClick = _a.onClick, biData = _a.biData, ariaLabel = _a["aria-label"];
    var isMobile = useContext(MobileContext);
    // track whether we've scrolled so we can hide the button when we have scrolled
    var initiallyScrolled = window && window.scrollY > 0;
    var _b = useState(initiallyScrolled), isScrolled = _b[0], setScrolled = _b[1];
    useScrollAware(function (_a) {
        var scrollTop = _a.scrollTop;
        var newlyScrolled = scrollTop > 0;
        if (newlyScrolled !== isScrolled) {
            setScrolled(newlyScrolled);
        }
    });
    // queue some complicated class changes to make the animation easier
    var _c = useTimedValue(!isScrolled && [
        ['', 200],
        [s['expanding'], 1000],
    ]), buttonClasses = _c[0], buttonReset = _c[1];
    var _d = useTimedValue(!isScrolled && [
        ['', 200],
        [s['fall-out'], 400],
        [s['hidden'], 500],
        [s['fall-in'], 400],
    ]), iconClasses = _d[0], iconReset = _d[1];
    // when we click the button, scroll to the target
    var targetToScrollTo = useRef(null);
    var wrappedClick = useCallback(function (e) {
        buttonReset();
        iconReset();
        var targetElement = targetToScrollTo.current;
        if (targetElement) {
            scrollIntoViewInViewportCenter(targetElement, isMobile, undefined, true);
        }
        return onClick && onClick(e);
    }, [buttonReset, iconReset, targetToScrollTo, isMobile, onClick]);
    if (typeof window === 'undefined') {
        return null;
    }
    var classes = classnames(s['root'], isScrolled && s['scrolled'], className);
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { className: classes },
            React.createElement(PlainButtonOrLink, { className: classnames(s['button'], buttonClasses), onClick: wrappedClick, biData: biData, "aria-label": ariaLabel }),
            React.createElement(ArrowDown, { className: classnames(s['icon'], iconClasses), size: IconSize.MEDIUM })),
        React.createElement("div", { ref: targetToScrollTo })));
};
ScrollDownButton.displayName = 'ScrollDownButton';
export default ScrollDownButton;
//# sourceMappingURL=ScrollDownButton.js.map